import { auth } from "@/lib/auth";
import {
  authInstance,
  endServerSession,
  isSessionUnavailable,
} from "@/lib/axios";
import { queryClient } from "@/lib/query-client";
import type { User } from "@/types/user";
import { isAxiosError } from "axios";
import { z } from "zod";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { useOrganizationStore } from "./useOrganizationStore";
import { useFeatureStore } from "./useFeatureStore";
import { useOrgSettingsStore } from "./useOrgSettingsStore";

interface LoginCredentials {
  email: string;
  password: string;
}

// Auth error bodies; each field tolerates an off-contract value by
// degrading to undefined so the next message candidate is tried.
const authErrorBodySchema = z.object({
  non_field_errors: z.array(z.string()).optional().catch(undefined),
  detail: z.string().optional().catch(undefined),
  error: z.string().optional().catch(undefined),
  details: z.record(z.string(), z.array(z.string())).optional().catch(undefined),
});

// 429 from auth/login/: `login_locked` (this account, after repeated wrong
// passwords) carries `retry_after` seconds; the per-IP throttle sends only
// `detail` and a Retry-After header (T-071).
const lockoutBodySchema = z.object({
  code: z.string().optional().catch(undefined),
  retry_after: z.coerce.number().optional().catch(undefined),
});

function tooManyAttemptsMessage(response: {
  data: unknown;
  headers?: Record<string, unknown>;
}): string {
  const body = lockoutBodySchema.safeParse(response.data);
  const seconds =
    (body.success ? body.data.retry_after : undefined) ??
    (Number(response.headers?.["retry-after"]) || undefined);
  const wait =
    seconds != null
      ? ` Try again in ${Math.max(1, Math.ceil(seconds / 60))} minute${Math.ceil(seconds / 60) === 1 ? "" : "s"}.`
      : " Try again later.";
  const what =
    body.success && body.data.code === "login_locked"
      ? "Too many failed attempts for this account."
      : "Too many sign-in attempts.";
  return what + wait;
}

interface AuthState {
  user: (User & { user_id: number }) | null;
  isLoading: boolean;
  isProfileLoading: boolean;
  error: string | null;
  login: (
    credentials: LoginCredentials,
  ) => Promise<{
    success: boolean;
    error?: string;
    fieldErrors?: Record<string, string[]>;
  }>;
  /** `endServerSession: false` when the server already refused the session
   *  (an expired refresh) -- see handleUnauthorized in lib/axios. */
  logout: (options?: { endServerSession?: boolean }) => void;
  /** `serverUnavailable`: the failure was the server's (network, timeout,
   *  5xx, or a refresh with no verdict), not a refused session. */
  getProfile: () => Promise<{
    success: boolean;
    user?: User;
    error?: string;
    serverUnavailable?: boolean;
  }>;
  /** Resolve the profile, fetching it once if it isn't loaded yet. */
  ensureProfile: () => Promise<(User & { user_id: number }) | null>;
  clearError: () => void;
}

/** In-flight `getProfile` call, so concurrent `ensureProfile` callers share one request. */
let profileRequest: Promise<unknown> | null = null;

export const useAuthStore = create<AuthState>()(
  devtools((set, get) => ({
    user: null,
    isLoading: false,
    isProfileLoading: false,
    error: null,

    login: async (credentials: LoginCredentials) => {
      set({ isLoading: true, error: null });

      try {
        const response = await authInstance.post(
          "/api/auth/login/",
          credentials,
        );
        // The refresh token arrives as an HttpOnly cookie, never in the body.
        const { token } = response.data;
        auth.setToken(token);
        set({ isLoading: false, error: null });

        const profileResult = await get().getProfile();
        if (!profileResult.success) {
          return { success: false, error: profileResult.error };
        }

        return { success: true };
      } catch (error) {
        let errorMsg = "Login failed";
        let fieldErrors: Record<string, string[]> | undefined;
        if (isAxiosError(error)) {
          if (error.code === "ERR_NETWORK") {
            errorMsg =
              error.message || "Network error: Please check your connection.";
          } else if (error.response?.status === 429) {
            errorMsg = tooManyAttemptsMessage(error.response);
          } else if (error.response) {
            const body = authErrorBodySchema.safeParse(error.response.data);

            if (error.response.status === 400 && body.success) {
              // LoginView returns { error, details: <serializer.errors> } on 400,
              // not the flat DRF { field: [...] } shape — parse both.
              fieldErrors = body.data.details;
              if (
                body.data.non_field_errors &&
                body.data.non_field_errors.length > 0
              ) {
                errorMsg = body.data.non_field_errors.join(" ");
              } else if (body.data.detail) {
                errorMsg = body.data.detail;
              } else if (body.data.error) {
                errorMsg = body.data.error;
              }
            } else if (body.success && body.data.error) {
              errorMsg = body.data.error;
            }
          }
        }

        set({ isLoading: false, error: errorMsg, user: null });
        return { success: false, error: errorMsg, fieldErrors };
      }
    },

    getProfile: async () => {
      set({ isProfileLoading: true, error: null });
      try {
        const token = auth.getToken();
        if (!token) throw new Error("No token found");
        const response = await authInstance.get("/api/users/profile/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const user = response.data;
        set({ user, isProfileLoading: false, error: null });
        return { success: true, user };
      } catch (error) {
        let errorMsg = "Failed to fetch profile";
        if (isAxiosError(error) && error.response) {
          const body = authErrorBodySchema.safeParse(error.response.data);
          if (body.success && body.data.detail) {
            errorMsg = body.data.detail;
          }
        }
        const serverUnavailable =
          isAxiosError(error) &&
          (isSessionUnavailable(error) ||
            !error.response ||
            error.response.status >= 500);
        set({ user: null, isProfileLoading: false, error: errorMsg });
        return { success: false, error: errorMsg, serverUnavailable };
      }
    },

    // Route guards run in `beforeLoad`, before any component effect has had a
    // chance to call `getProfile` — on a hard refresh the store is still empty.
    // Awaiting this keeps role checks from failing on a page reload.
    ensureProfile: async () => {
      if (get().user) return get().user;
      if (!auth.getToken()) return null;
      if (!profileRequest) {
        profileRequest = get().getProfile();
        try {
          await profileRequest;
        } finally {
          profileRequest = null;
        }
      } else {
        await profileRequest;
      }
      return get().user;
    },

    logout: (options) => {
      auth.resetToken();
      useOrganizationStore.getState().clearOrganizationStore();
      useFeatureStore.getState().clearFeatureStore();
      useOrgSettingsStore.getState().clearOrgSettingsStore();
      queryClient.clear();
      set({ user: null, error: null });

      // The server blacklists the refresh cookie, revokes every access token
      // of this login and clears the cookie. It needs no access token, so an
      // expired or already-revoked one can't make logout fail. Skipped when
      // the server already ended the session: the cookie jar may hold a
      // sibling tab's live cookie by then, and revoking it would log that tab
      // out as well (T-234).
      if (options?.endServerSession !== false) void endServerSession();
    },
    clearError: () => {
      set({ error: null });
    },
  })),
);
